/**
 * @file modalTemplates.ts
 *
 * HTML template generators for as-app transient modals (mesh transfer, history,
 * autoload, saving progress, errors). Also owns the ModalContent state type and
 * the buildModalHTML helper that maps ModalContent → panel HTML.
 */

import type { StoredMeshData, TransferSaveData } from '../../features/db/db.ts';
import { internalData } from '../../store/store.ts';
import { getAppSettings } from '../../data/appSettings.ts';

// ============================================================================
// ModalContent type
// ============================================================================

export interface ModalContent {
  type: 'saving' | 'success' | 'error' | 'none' | 'history' | 'autoload';
  data?: {
    blob?: Blob;
    error?: string;
    availableApps?: string[];
    meshes?: StoredMeshData[];
    autoLoadMesh?: StoredMeshData;
    status?: string;
    pendingStoreData?: TransferSaveData;
    /** Manual fallback shown by redirectToApp if the automatic redirect stalls. */
    link?: { href: string; label: string; newTab?: boolean };
  };
}

// ============================================================================
// Panel builder
// ============================================================================

/**
 * Build the full panel HTML (including the wrapper div) for a given ModalContent.
 * Returns an empty string for type 'none'.
 */
export function buildModalHTML(
  content: ModalContent,
  otherApps: string[],
  appInfoData: any,
  openTransferInNewTab: boolean = false,
  currentAppId?: string,
): string {
  if (content.type === 'none') return '';
  const panelId = `modal-panel-${content.type}`;
  let inner = '';
  switch (content.type) {
    case 'saving':
      inner = getSavingModalContent(content.data?.status, content.data?.link);
      break;
    case 'success':
      inner = getTransferToAppModal(otherApps, content.data?.blob, appInfoData, openTransferInNewTab, currentAppId);
      break;
    case 'error':
      inner = getErrorModalContent(content.data?.error);
      break;
    case 'history':
      inner = getHistoryModalContent(content.data?.meshes ?? []);
      break;
    case 'autoload':
      inner = getAutoLoadModalContent(content.data?.autoLoadMesh);
      break;
  }
  return `<div id="${panelId}" class="visible">${inner}</div>`;
}

// ============================================================================
// Template functions
// ============================================================================

export function getSavingModalContent(status?: string, link?: { href: string; label: string; newTab?: boolean }): string {
  const statusText = status ?? 'Saving mesh...';
  // A real, directly-clickable <a> (as opposed to redirectToApp's own synthetic
  // a.click()) for hosts that silently drop the automatic redirect — a click the
  // user made themselves is the strongest signal a host can be given. The href is
  // the real destination so it still behaves like a link (middle-click, copy
  // address), but handleTxModalClick takes the click over and re-issues it from a
  // light-DOM anchor: this one sits in as-app's shadow root, where the GrabCAD
  // host never sees it as a link at all.
  const linkRow = link ? /*html*/`
      <div class="modal-footer modal-footer--center">
        <a id="redirect-fallback-link" class="modal-btn modal-btn--secondary" href="${link.href}"${link.newTab ? ' target="_blank" rel="noopener noreferrer"' : ''}>${link.label}</a>
      </div>` : '';
  return /*html*/`
    <div id="saving-content" class="modal-panel">
      <div id="save-status">${statusText}</div>
      <div class="modal-spinner-row">
        <as-spinner style="--spinner-width: 3rem;"></as-spinner>
      </div>${linkRow}
    </div>
  `;
}

export function getTransferToAppModal(
  availableApps: string[],
  blob: Blob | undefined,
  appInfoData: any,
  openTransferInNewTab: boolean = false,
  currentAppId?: string,
): string {
  if (!blob) return getErrorModalContent();

  const selfApp = currentAppId && availableApps.includes(currentAppId) ? currentAppId : null;
  const otherAppIds = selfApp ? availableApps.filter(id => id !== currentAppId) : availableApps;

  const otherButtons = otherAppIds.map(appId => {
    const appName = appInfoData[appId]?.appNameDefaultText ?? appId;
    return `<div class="app-selection-button" data-app="${appId}">${appName}</div>`;
  }).join('');

  const selfButton = selfApp ? `
    <div class="modal-app-list-separator"></div>
    <div class="app-selection-button app-selection-button--self" data-app="${selfApp}">
      ${appInfoData[selfApp]?.appNameDefaultText ?? selfApp}
      <span class="app-selection-self-label">(this app)</span>
    </div>` : '';

  // Some environments (e.g. GrabCAD's embedded webview) have no meaningful
  // "open in new tab" behaviour, so the active env's app settings can suppress it.
  const allowNewTab = getAppSettings(internalData.appEnv).allowTransferNewTab;
  const newTabRow = allowNewTab ? /*html*/`
        <div class="h-flex modal-checkbox-row">
          <input type="checkbox" id="transfer-input-open-in-new-tab" name="transfer-input-open-in-new-tab" value="true" ${openTransferInNewTab ? 'checked' : ''}>
          <label for="transfer-input-open-in-new-tab"><as-text dict-key="modals.transfer.ready.open-in-new-tab">Open in new tab</as-text></label>
        </div>` : '';

  return /*html*/`
    <div id="success-content" class="modal-panel">
      <as-text id="save-status" dict-key="modals.transfer.ready.title">Mesh ready for transfer</as-text>
      <as-text dict-key="modals.transfer.ready.description">Your data may be lost! Please save your work before continuing.</as-text>
      <div class="modal-card">
        <h3><as-text dict-key="modals.transfer.ready.action-label">Transfer to:</as-text></h3>
        <div class="modal-app-list">
          ${otherButtons}${selfButton}
        </div>${newTabRow}
      </div>
    </div>
  `;
}

export function getErrorModalContent(error?: string): string {
  const errorText = error ?? 'Unknown error';
  return /*html*/`
    <div id="error-content" class="modal-panel">
      <div id="save-status" class="modal-error-text"><as-text dict-key="modals.error.prefix" default-text="Error: {0}" vars='${JSON.stringify([errorText])}'></as-text></div>
    </div>
  `;
}

export function getHistoryModalContent(meshes: StoredMeshData[]): string {
  const formatDate = (date: Date): string => new Date(date).toLocaleString();

  return /*html*/`
    <div id="history-content" class="modal-panel modal-panel--wide">
      <h2><as-text dict-key="modals.mesh.history.title">Mesh History</as-text></h2>
      <div class="modal-scroll-area">
        ${meshes.length === 0
          ? '<div class="modal-empty"><as-text dict-key="modals.mesh.history.empty">No mesh history found</as-text></div>'
          : `<table class="modal-table">
              <thead>
                <tr>
                  <th><as-text dict-key="modals.mesh.history.colName">Name</as-text></th>
                  <th><as-text dict-key="modals.mesh.history.colLastSave">Last Save</as-text></th>
                  <th><as-text dict-key="modals.mesh.history.colOrigin">Origin</as-text></th>
                </tr>
              </thead>
              <tbody>
                ${meshes.map(mesh => `
                  <tr>
                    <td>${mesh.meshName}</td>
                    <td>${formatDate(mesh.lastSave)}</td>
                    <td>${mesh.lastApp}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>`
        }
      </div>
      <div class="modal-footer">
        <button id="clear-mesh-history" class="modal-btn modal-btn--danger">
          <as-text dict-key="modals.mesh.history.clearAll">Clear All History</as-text>
        </button>
        <button class="close-modal modal-btn modal-btn--secondary">
          <as-text dict-key="modals.mesh.history.close">Close</as-text>
        </button>
      </div>
    </div>
  `;
}

export function getAutoLoadModalContent(mesh?: StoredMeshData): string {
  if (!mesh) return getErrorModalContent();

  const formatDate = (date: Date): string => new Date(date).toLocaleString();

  return /*html*/`
    <div id="autoload-content" class="modal-panel modal-panel--wide">
      <h2><as-text dict-key="modals.mesh.restore.title">Restore Mesh</as-text></h2>
      <div class="modal-card">
        <p><as-text dict-key="modals.mesh.restore.body">A previously saved mesh was found that can be loaded:</as-text></p>
        <div class="modal-detail-list">
          <div><strong><as-text dict-key="modals.mesh.restore.name">Name:</as-text></strong> ${mesh.meshName}</div>
          <div><strong><as-text dict-key="modals.mesh.restore.fromApp">From app:</as-text></strong> ${mesh.lastApp}</div>
          <div><strong><as-text dict-key="modals.mesh.restore.lastSaved">Last saved:</as-text></strong> ${formatDate(mesh.lastSave)}</div>
        </div>
        <p><as-text dict-key="modals.mesh.restore.question">Would you like to load this mesh?</as-text></p>
      </div>
      <div class="modal-footer">
        <button id="confirm-autoload" class="modal-btn modal-btn--success">
          <as-text dict-key="modals.mesh.restore.load">Load Mesh</as-text>
        </button>
        <button id="cancel-autoload" class="modal-btn modal-btn--secondary">
          <as-text dict-key="modals.mesh.restore.skip">Skip</as-text>
        </button>
      </div>
    </div>
  `;
}
